import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  Snackbar,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Refresh,
  AccountBalanceWallet,
  ReceiptLong,
  Payments,
  MonetizationOn,
  PlayArrow,
  Undo,
  WarningAmber,
  CheckCircle,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TypePill,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';
import { api } from '../../services/apiClient';
import { formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';

// ---------------------------------------------------------------------------
// Types — the /payment-allocations endpoints don't have a service module
// yet, so the shapes are declared locally (matching PaymentAllocation.js /
// paymentAllocation controller exactly) rather than fabricating a shared
// service that isn't this page's responsibility to own.
// ---------------------------------------------------------------------------

type Party = 'VENDOR' | 'CUSTOMER';
type PaymentType = 'VENDOR_PAYMENT' | 'CUSTOMER_RECEIPT';
type InvoiceType = 'PURCHASE_INVOICE' | 'SALES_INVOICE';

interface PartyBucket {
  count: number;
  value: number;
}

interface AllocationSummary {
  unallocatedPayments: { vendor: PartyBucket; customer: PartyBucket; totalValue: number };
  openInvoices: { purchase: PartyBucket; sales: PartyBucket; totalValue: number };
  engine: { allocationCount: number; paymentsTouched: number; invoicesTouched: number; totalAllocated: number };
}

interface UnallocatedPaymentRow {
  paymentType: PaymentType;
  paymentId: number;
  docNumber: string | null;
  paymentDate: string | null;
  partyId: number;
  partyName: string | null;
  status: string;
  netAmount: number;
  allocated: number;
  legacyReserved?: number;
  unallocated: number;
}

interface OutstandingInvoiceRow {
  invoiceType: InvoiceType;
  invoiceId: number;
  invoiceNumber: string;
  invoiceDate?: string | null;
  dueDate: string | null;
  status: string;
  totalAmount: number;
  paidAmount: number;
  engineAllocated: number;
  outstanding: number;
}

interface AllocationRow {
  id: number;
  payment_type: PaymentType;
  payment_id: number;
  invoice_type: InvoiceType;
  invoice_id: number;
  allocated_amount: number;
  allocation_date: string;
  notes: string | null;
  invoice_number?: string | null;
  payment_doc_number?: string | null;
}

interface PaymentDetail {
  payment: {
    paymentType: PaymentType;
    paymentId: number;
    docNumber: string | null;
    paymentDate: string | null;
    partyType: Party;
    partyId: number;
    partyName: string | null;
    poolAmount: number;
    status: string;
  };
  allocations: AllocationRow[];
  totalAllocated: number;
  remainingOnPayment: number;
}

interface AllocateResultLine {
  allocationId: number;
  invoiceType: InvoiceType;
  invoiceId: number;
  invoiceNumber: string;
  amount: number;
  newPaidAmount: number;
  newStatus: string;
  remainingOnInvoice: number;
}

interface AllocateResult {
  paymentType: PaymentType;
  paymentId: number;
  allocations: AllocateResultLine[];
  totalAllocatedNow: number;
  remainingOnPayment: number;
}

// Half a paisa — mirrors the server's EPSILON so client-side guards agree
// with the guards PaymentAllocation.allocate() actually enforces, instead of
// flagging a false positive on float/DECIMAL round-trip noise.
const EPSILON = 0.005;

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const daysToDue = (dueDate: string | null): number | null => {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  due.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86400000);
};

const dueTone = (days: number | null): StatusTone => {
  if (days === null) return 'neutral';
  if (days < 0) return 'error';
  if (days <= 3) return 'warning';
  return 'neutral';
};

const dueLabel = (dueDate: string | null): string => {
  const days = daysToDue(dueDate);
  if (days === null) return '—';
  if (days < 0) return `Overdue ${Math.abs(days)}d`;
  if (days === 0) return 'Due today';
  return `Due in ${days}d`;
};

const roundAmount = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

const invoiceTypeTint = (t: InvoiceType): string => (t === 'PURCHASE_INVOICE' ? '#dc2626' : '#059669');
const invoiceTypeLabel = (t: InvoiceType): string => (t === 'PURCHASE_INVOICE' ? 'Purchase Inv' : 'Sales Inv');

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const PaymentAllocationPage: React.FC = () => {
  const C = useControlRoomColors();

  const [party, setParty] = useState<Party>('VENDOR');

  const [summary, setSummary] = useState<AllocationSummary | null>(null);

  const [payments, setPayments] = useState<UnallocatedPaymentRow[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);

  const [selectedPayment, setSelectedPayment] = useState<UnallocatedPaymentRow | null>(null);

  const [outstanding, setOutstanding] = useState<OutstandingInvoiceRow[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);

  const [paymentDetail, setPaymentDetail] = useState<PaymentDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [amounts, setAmounts] = useState<Record<number, string>>({});
  const [allocating, setAllocating] = useState(false);
  const [deallocatingId, setDeallocatingId] = useState<number | null>(null);

  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // -------------------------------------------------------------------
  // Fetchers
  // -------------------------------------------------------------------

  const fetchSummary = useCallback(() => {
    api
      .get<AllocationSummary>('/payment-allocations/summary')
      .then(setSummary)
      .catch(() => {
        // Stat row stays hidden — never fabricate these numbers.
      });
  }, []);

  const fetchPayments = useCallback((p: Party) => {
    setLoadingPayments(true);
    setPaymentsError(null);
    api
      .get<{ party: Party; payments: UnallocatedPaymentRow[]; totalUnallocated: number }>(
        '/payment-allocations/unallocated-payments',
        { params: { party: p } }
      )
      .then((r) => setPayments(r.payments))
      .catch((err: any) => setPaymentsError(err.message || 'Failed to load unallocated payments'))
      .finally(() => setLoadingPayments(false));
  }, []);

  const fetchOutstanding = useCallback((p: Party, partyId: number) => {
    setLoadingInvoices(true);
    setInvoicesError(null);
    return api
      .get<{ party: Party; partyId: number; invoices: OutstandingInvoiceRow[]; totalOutstanding: number }>(
        '/payment-allocations/outstanding',
        { params: { party: p, partyId } }
      )
      .then((r) => setOutstanding(r.invoices))
      .catch((err: any) => setInvoicesError(err.message || 'Failed to load open invoices'))
      .finally(() => setLoadingInvoices(false));
  }, []);

  const fetchPaymentDetail = useCallback((paymentType: PaymentType, paymentId: number) => {
    setLoadingDetail(true);
    return api
      .get<PaymentDetail>(`/payment-allocations/payment/${paymentType}/${paymentId}`)
      .then(setPaymentDetail)
      .catch((err: any) => setToast({ message: err.message || 'Failed to load payment detail', severity: 'error' }))
      .finally(() => setLoadingDetail(false));
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchPayments(party);
  }, [party, fetchPayments]);

  // -------------------------------------------------------------------
  // Selection
  // -------------------------------------------------------------------

  const selectPayment = (row: UnallocatedPaymentRow) => {
    setSelectedPayment(row);
    setPaymentDetail(null);
    setAmounts({});
    fetchOutstanding(party, row.partyId);
    fetchPaymentDetail(row.paymentType, row.paymentId);
  };

  const handlePartyChange = (_: React.MouseEvent<HTMLElement>, value: Party | null) => {
    if (!value || value === party) return;
    setParty(value);
    setSelectedPayment(null);
    setPaymentDetail(null);
    setOutstanding([]);
    setAmounts({});
  };

  const refreshAll = () => {
    fetchSummary();
    fetchPayments(party);
    if (selectedPayment) {
      fetchOutstanding(party, selectedPayment.partyId);
      fetchPaymentDetail(selectedPayment.paymentType, selectedPayment.paymentId);
    }
  };

  // -------------------------------------------------------------------
  // Allocation form — client-side over-allocation guard
  // -------------------------------------------------------------------

  // Authoritative remaining balance comes from the payment-detail fetch
  // (server-computed, same math as the allocate() guard); fall back to the
  // list row's `unallocated` figure until that detail call resolves.
  const remainingOnPayment = paymentDetail?.remainingOnPayment ?? selectedPayment?.unallocated ?? 0;

  const enteredLines = useMemo(() => {
    return outstanding
      .map((inv) => {
        const raw = amounts[inv.invoiceId];
        const parsed = raw ? parseFloat(raw) : NaN;
        if (!Number.isFinite(parsed) || parsed <= 0) return null;
        return { inv, amount: roundAmount(parsed) };
      })
      .filter((x): x is { inv: OutstandingInvoiceRow; amount: number } => x !== null);
  }, [outstanding, amounts]);

  const allocatingTotal = useMemo(
    () => roundAmount(enteredLines.reduce((s, l) => s + l.amount, 0)),
    [enteredLines]
  );

  const linesOverInvoice = useMemo(
    () => enteredLines.filter((l) => l.amount > l.inv.outstanding + EPSILON),
    [enteredLines]
  );

  const overPayment = allocatingTotal > remainingOnPayment + EPSILON;

  const canAllocate =
    !!selectedPayment &&
    enteredLines.length > 0 &&
    !overPayment &&
    linesOverInvoice.length === 0 &&
    !allocating;

  const setAmount = (invoiceId: number, value: string) => {
    setAmounts((prev) => ({ ...prev, [invoiceId]: value }));
  };

  const handleAllocate = async () => {
    if (!selectedPayment || !canAllocate) return;
    setAllocating(true);
    try {
      const res = await api.post<AllocateResult>('/payment-allocations/allocate', {
        paymentType: selectedPayment.paymentType,
        paymentId: selectedPayment.paymentId,
        allocations: enteredLines.map((l) => ({
          invoiceType: l.inv.invoiceType,
          invoiceId: l.inv.invoiceId,
          amount: l.amount,
        })),
      });
      setToast({
        message: `Allocated ${formatIndianNumber(res.totalAllocatedNow)} across ${res.allocations.length} invoice${
          res.allocations.length === 1 ? '' : 's'
        }`,
        severity: 'success',
      });
      setAmounts({});
      fetchSummary();
      fetchPayments(party);
      await Promise.all([
        fetchOutstanding(party, selectedPayment.partyId),
        fetchPaymentDetail(selectedPayment.paymentType, selectedPayment.paymentId),
      ]);
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to allocate payment', severity: 'error' });
    } finally {
      setAllocating(false);
    }
  };

  const handleDeallocate = async (allocationId: number) => {
    setDeallocatingId(allocationId);
    try {
      await api.delete(`/payment-allocations/${allocationId}`);
      setToast({ message: 'Allocation reversed', severity: 'success' });
      fetchSummary();
      fetchPayments(party);
      if (selectedPayment) {
        await Promise.all([
          fetchOutstanding(party, selectedPayment.partyId),
          fetchPaymentDetail(selectedPayment.paymentType, selectedPayment.paymentId),
        ]);
      }
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to reverse allocation', severity: 'error' });
    } finally {
      setDeallocatingId(null);
    }
  };

  // -------------------------------------------------------------------
  // Party-scoped stat figures
  // -------------------------------------------------------------------

  const partyUnalloc = party === 'VENDOR' ? summary?.unallocatedPayments.vendor : summary?.unallocatedPayments.customer;
  const partyOpenInv = party === 'VENDOR' ? summary?.openInvoices.purchase : summary?.openInvoices.sales;

  return (
    <Box>
      <PageHeader
        title="Bill Allocation Workbench"
        subtitle="Match unallocated payments against open invoices — which bills does this payment cover, and how much is still open"
        actions={
          <>
            <ToggleButtonGroup size="small" exclusive value={party} onChange={handlePartyChange}>
              <ToggleButton value="VENDOR">Vendors</ToggleButton>
              <ToggleButton value="CUSTOMER">Customers</ToggleButton>
            </ToggleButtonGroup>
            <IconButton onClick={refreshAll} disabled={loadingPayments}>
              <Refresh />
            </IconButton>
          </>
        }
      />

      {summary && (
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatCard
              title={`Unallocated ${party === 'VENDOR' ? 'Payments' : 'Receipts'}`}
              value={formatIndianNumber(partyUnalloc?.value ?? 0)}
              icon={<AccountBalanceWallet />}
              color={C.accent}
              footer={
                <Typography variant="caption" color="text.secondary">
                  {partyUnalloc?.count ?? 0} awaiting allocation
                </Typography>
              }
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatCard
              title={`Open ${party === 'VENDOR' ? 'Purchase' : 'Sales'} Invoices`}
              value={formatIndianNumber(partyOpenInv?.value ?? 0)}
              icon={<ReceiptLong />}
              color={C.purple}
              footer={
                <Typography variant="caption" color="text.secondary">
                  {partyOpenInv?.count ?? 0} invoice{(partyOpenInv?.count ?? 0) === 1 ? '' : 's'} open
                </Typography>
              }
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatCard
              title="Engine Allocated (All-Time)"
              value={formatIndianNumber(summary.engine.totalAllocated)}
              icon={<Payments />}
              color={C.cyan}
              footer={
                <Typography variant="caption" color="text.secondary">
                  {summary.engine.allocationCount} allocation{summary.engine.allocationCount === 1 ? '' : 's'} recorded
                </Typography>
              }
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatCard
              title="Selected Payment Remaining"
              value={selectedPayment ? formatIndianNumber(remainingOnPayment) : null}
              icon={<MonetizationOn />}
              color={C.warn}
              footer={
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedPayment ? `${selectedPayment.partyName || '—'} · ${selectedPayment.docNumber || '—'}` : 'Nothing selected'}
                </Typography>
              }
            />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={2.5} alignItems="flex-start">
        {/* LEFT — Unallocated Payments */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Unallocated {party === 'VENDOR' ? 'Payments' : 'Receipts'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Select one to allocate it against open invoices
            </Typography>
          </Box>
          <TableSurface>
            <TableContainer sx={{ maxHeight: 640 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>{party === 'VENDOR' ? 'Vendor' : 'Customer'} / Doc #</TableCell>
                    <TableCell sx={headerCellSx}>Date</TableCell>
                    <TableCell sx={headerCellSx} align="right">Net</TableCell>
                    <TableCell sx={headerCellSx} align="right">Unallocated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingPayments ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 6, border: 0 }}>
                        <CircularProgress size={26} />
                      </TableCell>
                    </TableRow>
                  ) : paymentsError ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ border: 0 }}>
                        <ErrorState message={paymentsError} onRetry={() => fetchPayments(party)} />
                      </TableCell>
                    </TableRow>
                  ) : payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ border: 0 }}>
                        <TableEmptyState
                          title="Nothing unallocated"
                          hint={`Every ${party === 'VENDOR' ? 'vendor payment' : 'customer receipt'} is fully applied to invoices.`}
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map((row) => {
                      const isSelected =
                        !!selectedPayment &&
                        selectedPayment.paymentType === row.paymentType &&
                        selectedPayment.paymentId === row.paymentId;
                      return (
                        <TableRow
                          key={`${row.paymentType}-${row.paymentId}`}
                          hover
                          selected={isSelected}
                          onClick={() => selectPayment(row)}
                          sx={{ ...bodyRowSx, cursor: 'pointer' }}
                        >
                          <TableCell sx={{ py: 1.25 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.partyName || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.docNumber || `#${row.paymentId}`}
                            </Typography>
                          </TableCell>
                          <TableCell>{fmtDate(row.paymentDate)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatIndianNumber(row.netAmount)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                            {formatIndianNumber(row.unallocated)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Grid>

        {/* RIGHT — Open Invoices for the selected payment's party + allocation form + history */}
        <Grid size={{ xs: 12, md: 7 }}>
          {!selectedPayment ? (
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 3, minHeight: 300 }}>
              <TableEmptyState
                title="Select an unallocated payment from the list"
                hint="Its open invoices will appear here so you can decide how much covers what."
              />
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Selected payment header */}
              <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Payments fontSize="small" color="action" />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {selectedPayment.docNumber || `#${selectedPayment.paymentId}`}
                      </Typography>
                      <StatusDot label={selectedPayment.status} tone={selectedPayment.status === 'DRAFT' ? 'neutral' : 'success'} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {selectedPayment.partyName || '—'} · {fmtDate(selectedPayment.paymentDate)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Remaining unallocated
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: C.accent }}>
                      {loadingDetail ? <CircularProgress size={18} /> : formatIndianNumber(remainingOnPayment)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Open invoices + allocation inputs */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Open Invoices — {selectedPayment.partyName || '—'}
                </Typography>
                <TableSurface>
                  <TableContainer sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellSx}>Invoice #</TableCell>
                          <TableCell sx={headerCellSx}>Due</TableCell>
                          <TableCell sx={headerCellSx} align="right">Total</TableCell>
                          <TableCell sx={headerCellSx} align="right">Paid</TableCell>
                          <TableCell sx={headerCellSx} align="right">Outstanding</TableCell>
                          <TableCell sx={headerCellSx} align="right">Allocate</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loadingInvoices ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 6, border: 0 }}>
                              <CircularProgress size={26} />
                            </TableCell>
                          </TableRow>
                        ) : invoicesError ? (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ border: 0 }}>
                              <ErrorState message={invoicesError} onRetry={() => fetchOutstanding(party, selectedPayment.partyId)} />
                            </TableCell>
                          </TableRow>
                        ) : outstanding.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ border: 0 }}>
                              <TableEmptyState
                                title="No open invoices for this party"
                                hint="There is nothing left to apply this payment against."
                              />
                            </TableCell>
                          </TableRow>
                        ) : (
                          outstanding.map((inv) => {
                            const raw = amounts[inv.invoiceId] || '';
                            const parsed = raw ? parseFloat(raw) : NaN;
                            const lineOver = Number.isFinite(parsed) && parsed > inv.outstanding + EPSILON;
                            return (
                              <TableRow key={inv.invoiceId} sx={bodyRowSx}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{inv.invoiceNumber}</Typography>
                                </TableCell>
                                <TableCell>
                                  <StatusDot label={dueLabel(inv.dueDate)} tone={dueTone(daysToDue(inv.dueDate))} />
                                </TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {formatIndianNumber(inv.totalAmount)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {formatIndianNumber(inv.paidAmount)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                                  {formatIndianNumber(inv.outstanding)}
                                </TableCell>
                                <TableCell align="right">
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={raw}
                                    onChange={(e) => setAmount(inv.invoiceId, e.target.value)}
                                    error={lineOver}
                                    helperText={lineOver ? `Max ${formatIndianNumber(inv.outstanding)}` : ' '}
                                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    sx={{ width: 150, '& .MuiFormHelperText-root': { fontSize: '0.625rem', m: 0, mt: 0.25 } }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TableSurface>
              </Box>

              {/* Running total + guards */}
              {outstanding.length > 0 && (
                <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: overPayment ? 'error.main' : 'text.primary' }}
                    >
                      Allocating {formatIndianNumber(allocatingTotal)} of {formatIndianNumber(remainingOnPayment)} unallocated
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={allocating ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
                      disabled={!canAllocate}
                      onClick={handleAllocate}
                    >
                      {allocating ? 'Allocating…' : 'Allocate'}
                    </Button>
                  </Box>

                  {overPayment && (
                    <Alert severity="error" icon={<WarningAmber />} sx={{ mt: 1.5 }}>
                      Entered amounts exceed the unallocated balance on this payment by{' '}
                      {formatIndianNumber(roundAmount(allocatingTotal - remainingOnPayment))}. Reduce one or more lines
                      before allocating.
                    </Alert>
                  )}

                  {linesOverInvoice.length > 0 && (
                    <Alert severity="error" icon={<WarningAmber />} sx={{ mt: 1.5 }}>
                      <AlertTitle>Line exceeds invoice outstanding</AlertTitle>
                      {linesOverInvoice.map((l) => l.inv.invoiceNumber).join(', ')} — reduce the amount to at most the
                      invoice's outstanding balance.
                    </Alert>
                  )}
                </Paper>
              )}

              <Divider />

              {/* Allocation history for this payment */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Existing Allocations on This Payment
                </Typography>
                <TableSurface>
                  <TableContainer sx={{ maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellSx}>Invoice</TableCell>
                          <TableCell sx={headerCellSx}>Type</TableCell>
                          <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                          <TableCell sx={headerCellSx}>Date</TableCell>
                          <TableCell sx={headerCellSx}>Notes</TableCell>
                          <TableCell sx={headerCellSx} align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loadingDetail ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 5, border: 0 }}>
                              <CircularProgress size={22} />
                            </TableCell>
                          </TableRow>
                        ) : !paymentDetail || paymentDetail.allocations.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ border: 0 }}>
                              <TableEmptyState
                                title="No allocations yet"
                                hint="Enter amounts above and allocate to start covering invoices."
                              />
                            </TableCell>
                          </TableRow>
                        ) : (
                          paymentDetail.allocations.map((a) => (
                            <TableRow key={a.id} sx={bodyRowSx}>
                              <TableCell>{a.invoice_number || `#${a.invoice_id}`}</TableCell>
                              <TableCell>
                                <TypePill label={invoiceTypeLabel(a.invoice_type)} tint={invoiceTypeTint(a.invoice_type)} />
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                                {formatIndianNumber(a.allocated_amount)}
                              </TableCell>
                              <TableCell>{fmtDate(a.allocation_date)}</TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">{a.notes || '—'}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Reverse this allocation">
                                  <span>
                                    <Button
                                      size="small"
                                      color="error"
                                      startIcon={
                                        deallocatingId === a.id ? <CircularProgress size={14} color="inherit" /> : <Undo />
                                      }
                                      disabled={deallocatingId !== null}
                                      onClick={() => handleDeallocate(a.id)}
                                    >
                                      Deallocate
                                    </Button>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TableSurface>
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} icon={toast.severity === 'success' ? <CheckCircle /> : undefined}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default PaymentAllocationPage;
